import { GridColumnHeaderParams } from '@mui/x-data-grid-pro'
import styled from 'particles/themes/styled'
import React, { useRef } from 'react'
import InfoTooltip from 'components/InfoTooltip'
import { Tooltip } from '@mui/material'
import ParticleTooltip from 'particles/Tooltip'
import Icon from 'particles/Icon'
import { useNewTranslation } from 'hooks'

interface HeaderProps {
    params: GridColumnHeaderParams
    width: number
    tooltipLabel?: string
}

export const getMultilineHeader = ({ params, width, tooltipLabel }: HeaderProps) => {
    const headerRef = useRef<HTMLDivElement>(null)
    const isOverflow = (() => {
        if (!headerRef.current) {
            return false
        }
        return headerRef.current.scrollHeight > headerRef.current.clientHeight
    })()

    return (
        <HeaderContainer width={width}>
            <Tooltip title={isOverflow ? params.colDef.headerName ?? '' : ''}>
                <MultilineHeader ref={headerRef}>{params.colDef.headerName}</MultilineHeader>
            </Tooltip>
            {tooltipLabel && <InfoTooltip iconColor="neutralAction" label={tooltipLabel} />}
        </HeaderContainer>
    )
}

interface CustomEngagementHeaderProps extends HeaderProps {
    expandEngagement: boolean
    setExpandEngagement: (v: boolean) => void
}

export const CustomEngagementHeader = ({
    params,
    expandEngagement,
    setExpandEngagement,
}: CustomEngagementHeaderProps) => {
    const [t] = useNewTranslation()
    const headerRef = useRef<HTMLDivElement>(null)
    const isOverflow = (() => {
        if (!headerRef.current) {
            return false
        }
        return headerRef.current.scrollHeight > headerRef.current.clientHeight
    })()
    return (
        <CustomExpandHeader ref={headerRef} width={params.colDef.width}>
            <Tooltip title={isOverflow ? params.colDef.headerName ?? '' : ''}>
                <span className="header-text">{params.colDef.headerName}</span>
            </Tooltip>

            <ParticleTooltip label={t('tooltips.expandProfileDetails')}>
                <CustomExpandButton
                    onClick={() => {
                        setExpandEngagement(!expandEngagement)
                        if (!expandEngagement) {
                            setTimeout(() => {
                                const gridElement = document.querySelector('.MuiDataGrid-virtualScroller')
                                if (gridElement) {
                                    gridElement.scrollLeft = gridElement.scrollWidth
                                }
                            }, 100)
                        }
                    }}
                    className="expand-icon"
                >
                    <Icon name={expandEngagement ? 'caretLeft' : 'caretRight'} size="16px" />
                </CustomExpandButton>
            </ParticleTooltip>
        </CustomExpandHeader>
    )
}

const HeaderContainer = styled.div<{ width: number }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    width: ${({ width }) => width}px;
`

const MultilineHeader = styled.span`
    display: inline-block;
    max-width: 100%;
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.2em;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`

const CustomExpandHeader = styled.div<{ width?: number }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: ${({ width }) => width || '0'}px;

    .header-text {
        width: calc(100% - 24px);
        white-space: normal;
        word-wrap: break-word;
        line-height: 1.2em;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`

const CustomExpandButton = styled.button`
    width: 16px;
    height: 40px;
    gap: 10px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    border: 0;
    outline: 0;
    padding: 0px;
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.colors.surface.neutralActionPastelHover};
    }

    svg {
        flex-shrink: 0;
    }
`
